import React, { useState } from 'react';
import { Box, Grid, Alert, Typography, Button, Chip, Tooltip, IconButton } from '@mui/material';

import DetectionCard from '../cards/DetectionCard';
import { REPORTS_DETECTION_TYPES } from '../../utils/constants';

import PeopleIcon        from "@mui/icons-material/People";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import PersonIcon        from "@mui/icons-material/Person";
import SecurityIcon      from "@mui/icons-material/Security";
import ArrowBackIcon     from "@mui/icons-material/ArrowBack";
import OpenInFullIcon    from "@mui/icons-material/OpenInFull";
import CloseFullscreenIcon from "@mui/icons-material/CloseFullscreen";

const ReportResults = ({ 
  reportData, 
  selectedDetections,
  selectedPriorities,
  dataLoaded, 
  totalEvents,
  pagination,
  onLoadMore,
  isMobile,
  isTablet,
  typeSpecificCounts,
  typeLoadingStatus,
  onTypeSpecificLoadMore,
  updateEventPriority,
  getEventPriority,
  onEventUpdated,
  setReportData
}) => {
  // State to track which detection card is currently selected/expanded
  const [selectedCard, setSelectedCard] = useState(null);

  // FIXED: Use more specific priority tracking with multiple identifiers
  const [reportPriorityMap, setReportPriorityMap] = useState(new Map());

  // FIXED: Improved event ID generation with better uniqueness
  const getEventId = (event) => {
    if (typeof event === 'object') {
      // Create a comprehensive unique identifier
      const eventId = event.eventId || event.id || event.dbId;
      const timestamp = event.timestamp || '';
      const cameraIp = event.cameraIp || '';
      const eventType = event.eventType || '';
      
      // If we have eventId, use it as primary, but enhance with other fields for uniqueness
      if (eventId) {
        return `${eventId}_${timestamp}_${cameraIp}_${eventType}`.replace(/undefined/g, '').replace(/_+/g, '_');
      }
      
      // Fallback to timestamp-based ID
      return `${timestamp}_${cameraIp}_${eventType}`.replace(/undefined/g, '').replace(/_+/g, '_');
    }
    return event;
  };

  // UPDATED: Enhanced unique key generation to include event type for PD/ID distinction
  const createUniqueEventKey = (event) => {
    if (!event) return '';
    
    const eventId = event.eventId || event.id || event.dbId || '';
    const timestamp = event.timestamp || '';
    const cameraIp = event.cameraIp || '';
    const location = event.location || '';
    const area = event.area || '';
    const eventType = event.eventType || event.detectionType || ''; // Include detection type
    
    // Create a more specific composite key that includes event type
    const compositeKey = `${eventId}|${timestamp}|${cameraIp}|${location}|${area}|${eventType}`;
    
    // Remove empty parts and clean up
    const cleanKey = compositeKey
      .split('|')
      .filter(part => part && part !== 'undefined' && part !== 'null')
      .join('|');
    
    console.log('🔑 Generated key for event:', {
      eventId: eventId.substring(0, 20) + '...',
      key: cleanKey.substring(0, 50) + '...',
      location: location,
      eventType: eventType // Log event type for debugging
    });
    
    return cleanKey || `fallback_${Date.now()}_${Math.random()}`;
  };

  // UPDATED: Process and organize report data to handle ID events
  const updateReportEventPriority = async (targetEvent, priorityData) => {
    try {
      console.log('🔧 PRIORITY UPDATE START:', {
        eventId: targetEvent.eventId || targetEvent.id,
        location: targetEvent.location,
        severity: priorityData.severity,
        eventType: targetEvent.detectionType // Log event type (PD or ID)
      });
      
      const targetEventKey = createUniqueEventKey(targetEvent);
      const targetEventId = getEventId(targetEvent);
      
      if (!targetEventId) {
        console.error('❌ No eventId found for event:', targetEvent);
        return;
      }
      
      // Update the database through the original function
      if (updateEventPriority) {
        await updateEventPriority(targetEvent, priorityData);
      }
      
      // Update local priority map with the specific key
      setReportPriorityMap(prev => {
        const newMap = new Map(prev);
        newMap.set(targetEventKey, {
          severity: priorityData.severity || null,
          remarks: priorityData.remarks || [],
          updatedAt: new Date().toISOString()
        });
        return newMap;
      });

      // CRITICAL FIX: More precise state update to prevent affecting other events
      if (setReportData) {
        setReportData(prevReportData => {
          const updatedReportData = { ...prevReportData };
          let updateCount = 0;
          
          // FIXED: Process only report detection types
          REPORTS_DETECTION_TYPES.forEach(type => {
            if (updatedReportData[type.id] && Array.isArray(updatedReportData[type.id])) {
              
              updatedReportData[type.id] = updatedReportData[type.id].map((eventItem, index) => {
                const itemKey = createUniqueEventKey(eventItem);
                
                // EXACT MATCH CHECK - only update if it's the exact same event
                if (itemKey === targetEventKey) {
                  console.log('✅ EXACT MATCH FOUND:', {
                    type: type.id,
                    index: index,
                    eventId: eventItem.eventId || eventItem.id,
                    location: eventItem.location,
                    eventType: eventItem.detectionType, // Log event type
                    updating: `${eventItem.severity} -> ${priorityData.severity}`
                  });
                  
                  updateCount++;
                  
                  // Create updated event - ONLY modify the matching event
                  const updatedEvent = {
                    ...eventItem,
                    severity: priorityData.severity || eventItem.severity,
                    priorityUpdated: true,
                    priorityUpdatedAt: new Date().toISOString()
                  };

                  // Handle remarks properly
                  if (priorityData.remarks && priorityData.remarks.length > 0) {
                    const existingRemarks = Array.isArray(eventItem.remarks) ? eventItem.remarks : [];
                    const newRemarks = Array.isArray(priorityData.remarks) ? priorityData.remarks : [priorityData.remarks];
                    
                    // Merge remarks without duplicates
                    const mergedRemarks = [...existingRemarks];
                    newRemarks.forEach(newRemark => {
                      const isDuplicate = existingRemarks.some(existing => 
                        existing.comment === newRemark.comment && 
                        existing.user === newRemark.user
                      );
                      if (!isDuplicate) {
                        mergedRemarks.push(newRemark);
                      }
                    });
                    
                    updatedEvent.remarks = mergedRemarks;
                  }
                  
                  return updatedEvent;
                } else {
                  // CRITICAL: Return all other events UNCHANGED
                  return eventItem;
                }
              });
            }
          });
          
          console.log('🔄 Update summary:', {
            eventsUpdated: updateCount,
            targetKey: targetEventKey.substring(0, 50) + '...'
          });
          
          if (updateCount === 0) {
            console.warn('⚠️ No events were updated - possible key mismatch');
          } else if (updateCount > 1) {
            console.warn('⚠️ Multiple events updated - possible duplicate keys');
          }
          
          return updatedReportData;
        });
      }

      // Notify parent component
      if (onEventUpdated) {
        onEventUpdated({
          eventId: targetEventId,
          eventKey: targetEventKey,
          ...priorityData
        });
      }
      
      console.log('✅ PRIORITY UPDATE COMPLETE');
      
    } catch (error) {
      console.error('❌ Priority update failed:', error);
    }
  };

  // FIXED: Improved priority retrieval
  const getReportEventPriority = (event) => {
    if (!event) return null;
    
    const eventKey = createUniqueEventKey(event);
    const eventId = getEventId(event);
    
    // Check local priority map first
    if (reportPriorityMap.has(eventKey)) {
      const priorityData = reportPriorityMap.get(eventKey);
      return priorityData;
    }
    
    // Check if event has its own priority data
    if (event.severity) {
      const eventPriorityData = {
        severity: event.severity,
        remarks: event.remarks || []
      };
      return eventPriorityData;
    }
    
    // Fall back to the main priority system
    if (getEventPriority) {
      const priorityData = getEventPriority(event);
      if (priorityData) {
        return priorityData;
      }
    }
    
    return null;
  };

  // Get the appropriate icon component based on the detection type
  const getIconComponent = (iconName) => {
    switch (iconName) {
      case "PeopleIcon": return PeopleIcon;
      case "DirectionsCarIcon": return DirectionsCarIcon;
      case "PersonIcon": return PersonIcon;
      case "SecurityIcon": return SecurityIcon;
      default: return SecurityIcon;
    }
  };
  
  // Get data for a specific detection type
  const getDataForDetectionType = (detectionType) => {
    return reportData[detectionType] || [];
  };

  // Handle card selection/expansion
  const handleCardClick = (cardId) => {
    if (selectedCard !== cardId) {
      setSelectedCard(cardId);
    }
  };

  // Handle back button click (mobile only)
  const handleBackClick = () => {
    setSelectedCard(null);
  };

  // Calculate total events displayed
  const totalEventsDisplayed = Object.values(reportData).reduce(
    (total, events) => total + events.length, 0
  );
  
  // Check if all events have been loaded
  const allEventsLoaded = totalEventsDisplayed >= totalEvents;
  const safeSelectedDetections = selectedDetections || [];

  // FIXED: Use REPORTS_DETECTION_TYPES for filtering
  const detectionTypesToRender = 
    (safeSelectedDetections.includes("All") || safeSelectedDetections.length === 0)
      ? REPORTS_DETECTION_TYPES 
      : REPORTS_DETECTION_TYPES.filter(type => safeSelectedDetections.includes(type.id));

  // ── Expanded view (any screen size) ──────────────────────────────────────
  if (selectedCard) {
    const selectedType = REPORTS_DETECTION_TYPES.find(t => t.id === selectedCard);
    const data         = getDataForDetectionType(selectedCard);
    const IconComp     = getIconComponent(selectedType?.icon);
    const isTypeLoading = typeLoadingStatus?.[selectedCard] ?? false;
    const totalTypeCount = typeSpecificCounts[selectedCard] || 0;
    const title = selectedType?.title || selectedCard;

    return (
      <>
        {/* ── Expanded header bar ── */}
        <Box
          sx={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            mb: 2, px: 1,
            pb: 1.5, borderBottom: '1px solid rgba(255,255,255,0.07)',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Button
              startIcon={<ArrowBackIcon sx={{ fontSize: 16 }} />}
              onClick={handleBackClick}
              size="small"
              sx={{
                color: '#1ABC9C', textTransform: 'none', fontWeight: 600,
                fontSize: '0.82rem', px: 1.5, py: 0.6,
                border: '1px solid rgba(26,188,156,0.3)',
                borderRadius: '8px',
                bgcolor: 'rgba(26,188,156,0.06)',
                '&:hover': { bgcolor: 'rgba(26,188,156,0.14)', borderColor: '#1ABC9C' },
              }}
            >
              All Cards
            </Button>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
              <CloseFullscreenIcon sx={{ fontSize: 14, color: 'rgba(255,255,255,0.35)' }} />
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.8rem' }}>
                Expanded —
              </Typography>
              <Typography variant="body2" sx={{ color: '#FFA726', fontWeight: 700, fontSize: '0.8rem' }}>
                {title}
              </Typography>
            </Box>
          </Box>

          {/* Quick-switch chips for other types */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.6 }}>
            {detectionTypesToRender
              .filter(t => t.id !== selectedCard)
              .map(t => {
                const cnt = getDataForDetectionType(t.id).length;
                if (cnt === 0) return null;
                return (
                  <Chip
                    key={t.id}
                    label={`${t.title} (${cnt})`}
                    size="small"
                    onClick={() => setSelectedCard(t.id)}
                    sx={{
                      fontSize: '0.7rem', height: 22, cursor: 'pointer',
                      bgcolor: 'rgba(255,255,255,0.06)',
                      color: 'rgba(255,255,255,0.55)',
                      border: '1px solid rgba(255,255,255,0.1)',
                      '&:hover': { bgcolor: 'rgba(26,188,156,0.15)', color: '#1ABC9C', borderColor: 'rgba(26,188,156,0.4)' },
                    }}
                  />
                );
              })}
          </Box>
        </Box>

        {/* ── Expanded card (full width) ── */}
        <DetectionCard
          title={title}
          icon={IconComp}
          selected={true}
          onClick={() => {}}
          data={data}
          onDelete={() => {}}
          isReportView={true}
          onLoadMore={() => onTypeSpecificLoadMore(selectedCard)}
          isLoadingMore={isTypeLoading}
          hasMore={data.length < totalTypeCount}
          displayCount={data.length}
          totalCount={totalTypeCount}
          updateEventPriority={updateReportEventPriority}
          getEventPriority={getReportEventPriority}
          onEventUpdated={onEventUpdated}
        />
      </>
    );
  }

  // ── Normal grid view ──────────────────────────────────────────────────────
  return (
    <>
      {!dataLoaded && Object.values(reportData).every(d => d.length === 0) && (
        <Alert severity="info" sx={{ mb: 2, backgroundColor: '#2c3e50', color: 'white' }}>
          No data available for the selected time period and filters.
        </Alert>
      )}

      {/* Summary row */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="body2" sx={{ color: '#1ABC9C', fontSize: '0.82rem' }}>
          Showing {totalEventsDisplayed} of {totalEvents} events
          {allEventsLoaded && ' (all loaded)'}
        </Typography>
        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.72rem' }}>
          Click <OpenInFullIcon sx={{ fontSize: 11, verticalAlign: 'middle', mx: 0.3 }} /> on any card to expand it
        </Typography>
      </Box>

      <Grid container spacing={isMobile ? 1 : 2}>
        {detectionTypesToRender.map((type) => {
          const data           = getDataForDetectionType(type.id);
          const IconComp       = getIconComponent(type.icon);
          const isTypeLoading  = typeLoadingStatus?.[type.id] ?? false;
          const totalTypeCount = typeSpecificCounts[type.id] || 0;
          const cols           = detectionTypesToRender.length === 1 ? 12 : (isMobile ? 12 : 6);

          return (
            <Grid item xs={12} md={cols} key={type.id}>
              {/* ── Card wrapper with expand button ── */}
              <Box sx={{ position: 'relative' }}>
                {/* Expand button — top-right corner */}
                <Tooltip title={`Expand ${type.title}`} placement="top">
                  <IconButton
                    size="small"
                    onClick={() => setSelectedCard(type.id)}
                    sx={{
                      position: 'absolute', top: 10, right: 10, zIndex: 10,
                      width: 28, height: 28,
                      bgcolor: 'rgba(26,188,156,0.12)',
                      border: '1px solid rgba(26,188,156,0.25)',
                      color: '#1ABC9C',
                      opacity: 0,
                      transition: 'opacity 0.18s, background 0.18s',
                      '&:hover': { bgcolor: 'rgba(26,188,156,0.28)' },
                      '.card-wrapper:hover &': { opacity: 1 },
                    }}
                    className="expand-btn"
                  >
                    <OpenInFullIcon sx={{ fontSize: 13 }} />
                  </IconButton>
                </Tooltip>

                <Box
                  className="card-wrapper"
                  sx={{
                    '&:hover .expand-btn': { opacity: 1 },
                  }}
                >
                  <DetectionCard
                    title={type.title}
                    icon={IconComp}
                    selected={false}
                    onClick={() => {}}
                    data={data}
                    onDelete={() => {}}
                    isReportView={true}
                    onLoadMore={() => onTypeSpecificLoadMore(type.id)}
                    isLoadingMore={isTypeLoading}
                    hasMore={data.length < totalTypeCount}
                    displayCount={data.length}
                    totalCount={totalTypeCount}
                    updateEventPriority={updateReportEventPriority}
                    getEventPriority={getReportEventPriority}
                    onEventUpdated={onEventUpdated}
                  />
                </Box>
              </Box>
            </Grid>
          );
        })}
      </Grid>
    </>
  );
};

export default ReportResults;